import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, ArrowRight, Sparkles, CheckCircle2, XCircle, FileText, ShieldCheck, PoundSterling, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const toArea = (slug) => (slug || '').replace(/-/g, '_');
const field = 'w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40 bg-white';

// ---- Public questionnaire + pay ----
export const ProcurementReportForm = () => {
  const { slug } = useParams();
  const area = toArea(slug);
  const [meta, setMeta] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [contact, setContact] = useState({ name: '', email: '', organisation: '' });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/procurement/questionnaire/${area}`)
      .then((r) => setMeta(r.data)).catch(() => setNotFound(true));
  }, [area]);

  if (notFound) return <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="report-area-not-found"><p className="text-gray-500">This report isn't available.</p><Link to="/procurement" className="text-[#00A79D] font-semibold mt-3 inline-block">Back to procurement</Link></div>;
  if (!meta) return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  const setAns = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.email.trim()) { toast.error('Work email is required'); return; }
    const missing = meta.questions.filter((q) => q.required && !answers[q.key]);
    if (missing.length) { toast.error(`Please answer: ${missing.map((m) => m.label).join(', ')}`); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/procurement/report`, {
        area, email: contact.email.trim(), name: contact.name.trim() || null,
        organisation: contact.organisation.trim() || null, answers, origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
      else { toast.error('Could not start checkout'); setBusy(false); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Something went wrong'); setBusy(false); }
  };

  return (
    <div className="bg-[#F8F9FA] min-h-screen" data-testid="page-report-form">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Link to={`/procurement/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00A79D] mb-6" data-testid="report-back"><ArrowLeft className="w-4 h-4" /> Back</Link>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-4"><Sparkles className="w-3.5 h-3.5" /> Personalised report</span>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1A3A5F]">Your personalised {meta.label.toLowerCase()} report</h1>
        <p className="text-lg text-gray-600 mt-3">Answer a few questions and our team will prepare tailored recommendations and options for your service, reviewed by Care Tech Compare before it reaches you.</p>
        <div className="mt-5 inline-flex items-baseline gap-2 bg-white border border-[#E5E7EB] rounded-lg px-4 py-3">
          <span className="text-2xl font-extrabold text-[#1A3A5F]">£{Number(meta.fee).toFixed(0)}</span>
          <span className="text-sm text-gray-500">one-off, includes expert review</span>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="report-form">
          {meta.questions.map((q) => (
            <div key={q.key} data-testid={`report-q-${q.key}`}>
              <label className="text-sm font-semibold text-[#1A3A5F]">{q.label}{q.required && ' *'}</label>
              {q.type === 'select' ? (
                <select value={answers[q.key] || ''} onChange={(e) => setAns(q.key, e.target.value)} className={`${field} mt-1.5`} data-testid={`report-input-${q.key}`}>
                  <option value="">Please select</option>
                  {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : q.type === 'textarea' ? (
                <textarea rows={3} value={answers[q.key] || ''} onChange={(e) => setAns(q.key, e.target.value)} className={`${field} mt-1.5`} data-testid={`report-input-${q.key}`} />
              ) : (
                <input type={q.type === 'number' ? 'number' : 'text'} value={answers[q.key] || ''} onChange={(e) => setAns(q.key, e.target.value)} className={`${field} mt-1.5`} data-testid={`report-input-${q.key}`} />
              )}
            </div>
          ))}
          <div className="border-t border-[#E5E7EB] pt-5 space-y-3">
            <input type="text" placeholder="Your name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className={field} data-testid="report-name" />
            <input type="text" placeholder="Organisation" value={contact.organisation} onChange={(e) => setContact({ ...contact, organisation: e.target.value })} className={field} data-testid="report-org" />
            <input type="email" required placeholder="Work email *" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={field} data-testid="report-email" />
          </div>
          <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3.5 rounded-md transition-colors disabled:opacity-60" data-testid="report-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PoundSterling className="w-4 h-4" />} Pay £{Number(meta.fee).toFixed(0)} & generate my report
          </button>
          <p className="text-[11px] text-gray-400 text-center">Secure payment via Stripe. Your report is drafted after payment and reviewed by our team before you receive it.</p>
        </form>
      </div>
    </div>
  );
};

// ---- Payment return ----
export const ProcurementReportSuccess = () => {
  const [sp] = useSearchParams();
  const sessionId = sp.get('session_id');
  const [state, setState] = useState('checking');
  const [token, setToken] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) { setState('pending'); return; }
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/public/procurement/status/${sessionId}`);
        if (data.access_token) setToken(data.access_token);
        if (data.payment_status === 'paid') { setState('paid'); return; }
        if (data.status === 'expired') { setState('error'); return; }
        setTimeout(poll, 2000);
      } catch { setState('error'); }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="bg-[#F8F9FA] min-h-screen" data-testid="page-report-success">
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-10">
          {state === 'checking' && <><Loader2 className="w-10 h-10 text-[#00A79D] mx-auto mb-4 animate-spin" /><p className="font-semibold text-lg text-[#1A3A5F]" data-testid="report-status-checking">Confirming your payment…</p></>}
          {state === 'paid' && <><CheckCircle2 className="w-12 h-12 text-[#00A79D] mx-auto mb-4" /><h1 className="font-heading text-2xl font-extrabold text-[#1A3A5F]" data-testid="report-status-paid">Payment received, thank you.</h1><p className="text-gray-600 mt-3">Our team is preparing your personalised report now. We'll email you the moment it's ready.</p>{token && <Link to={`/procurement/report/${token}`} className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md mt-6 transition-colors" data-testid="report-view-link">Track my report <ArrowRight className="w-4 h-4" /></Link>}</>}
          {state === 'pending' && <><Loader2 className="w-10 h-10 text-amber-500 mx-auto mb-4" /><p className="font-semibold text-lg text-[#1A3A5F]" data-testid="report-status-pending">Payment is processing.</p><p className="text-gray-600 mt-2">We'll email your confirmation shortly.</p></>}
          {state === 'error' && <><XCircle className="w-10 h-10 text-red-500 mx-auto mb-4" /><p className="font-semibold text-lg text-[#1A3A5F]" data-testid="report-status-error">We couldn't confirm this payment.</p></>}
        </div>
      </div>
    </div>
  );
};

// ---- Buyer report view (secure token) ----
export const ProcurementReportView = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/procurement/report/${token}`)
      .then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [token]);

  if (notFound) return <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="report-view-not-found"><p className="text-gray-500">Report not found.</p></div>;
  if (!data) return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  const r = data.report;
  return (
    <div className="bg-[#F8F9FA] min-h-screen" data-testid="page-report-view">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-4"><FileText className="w-3.5 h-3.5" /> {data.label} report</span>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1A3A5F]">Your personalised {data.label.toLowerCase()} report</h1>
        {data.organisation && <p className="text-gray-500 mt-2">Prepared for {data.organisation}</p>}

        {!data.ready ? (
          <div className="mt-8 bg-white border border-[#E5E7EB] rounded-lg p-8 text-center" data-testid="report-not-ready">
            <Loader2 className="w-8 h-8 text-[#00A79D] mx-auto mb-3 animate-spin" />
            <p className="font-semibold text-[#1A3A5F]">Your report is being prepared and reviewed by our team.</p>
            <p className="text-sm text-gray-600 mt-1">We'll email you as soon as it's ready. Please check back shortly.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-8" data-testid="report-content">
            <section className="bg-white border border-[#E5E7EB] rounded-lg p-6">
              <h2 className="font-heading text-lg font-semibold text-[#1A3A5F] mb-2">Summary</h2>
              <p className="text-gray-700 leading-relaxed">{r.summary}</p>
              {r.estimated_savings && <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#00A79D]"><TrendingUp className="w-4 h-4" /> {r.estimated_savings}</p>}
            </section>

            {(r.recommendations || []).length > 0 && (
              <section>
                <h2 className="font-heading text-lg font-semibold text-[#1A3A5F] mb-3">Recommended options</h2>
                <div className="space-y-3">
                  {r.recommendations.map((rec, i) => (
                    <div key={i} className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid={`report-rec-${i}`}>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-[#1A3A5F]">{rec.supplier_name}</p>
                        {rec.supplier_slug && <Link to={`/suppliers/${rec.supplier_slug}`} className="text-xs font-semibold text-[#00A79D] inline-flex items-center gap-1">View listing <ArrowRight className="w-3 h-3" /></Link>}
                      </div>
                      <p className="text-sm text-gray-600 mt-1.5">{rec.why}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {(r.guidance || []).length > 0 && (
              <section className="bg-white border border-[#E5E7EB] rounded-lg p-6">
                <h2 className="font-heading text-lg font-semibold text-[#1A3A5F] mb-3">Practical guidance</h2>
                <ul className="space-y-2">
                  {r.guidance.map((g, i) => <li key={i} className="flex items-start gap-2 text-gray-700"><CheckCircle2 className="w-4 h-4 text-[#00A79D] mt-1 shrink-0" /> {g}</li>)}
                </ul>
              </section>
            )}

            {r.next_steps && (
              <section className="bg-[#00A79D]/5 border border-[#00A79D]/20 rounded-lg p-6">
                <h2 className="font-heading text-lg font-semibold text-[#00A79D] mb-2">Next steps</h2>
                <p className="text-gray-700">{r.next_steps}</p>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
